import { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../../context/LanguageContext';
import { normalizeSupportedLanguage } from '../../utils/createScopedI18n';
import { tasksI18n } from './i18n';
import ExpandablePanel from '../../components/ExpandablePanel/ExpandablePanel';
import FilterChips from '../../components/FilterChips/FilterChips';
import CardStats from '../../components/CardStats/CardStats';
import DetailTaskModal from './DetailTaskModal';
import ManageTaskModal from './ManageTaskModal';
import ApprovalFlowModal from '../ApprovalRequest/ApprovalFlowModal';
import {
  fetchPagedTasks,
  fetchTaskCounts,
  fetchTaskDetailsById,
  fetchTaskStates,
  fetchTaskPriorities,
  getUserCases,
  fetchAllSystemUsers,
  fetchTaskTitles,
  createTask,
  removeUserFromTask,
  exportTasksExcel,
} from '../../services/TaskService';
import './task.css';
import { getCurrentUser } from '../../services/authService';
import { getAllCourts } from '../../services/courtServices';
import { getCreateInfo } from 'services/caseServices';
import {
  WORKFLOW_STATUS,
  NODE_TYPE,
  getWorkflowInstance,
  getWorkflowInstanceByReference,
  invalidateWorkflowReferenceCache,
} from '../../services/workflowService';
import usePermissionsTasks from 'permissions/modules/tasks/usePermissionsTasks';

const buildStatusOptions = (t) => [
  { label: t('status_all'), value: 'todos', backendId: null },
  { label: t('status_in_progress'), value: 'en_progreso', backendId: 2 },
  { label: t('status_approved'), value: 'aprobada', backendId: 1 },
  { label: t('status_rejected'), value: 'rechazada', backendId: 4 },
  { label: t('status_unassigned'), value: 'unassigned', backendId: 5 },
];
const WORKFLOW_REFERENCE_TYPE_TASK = 'TASK';

const normalizeStatusToken = (value) =>
  String(value || '')
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/\s+/g, '_');

const resolveTaskStatus = (taskItem) => {
  const rawStatus = normalizeStatusToken(taskItem?.nombreEstado);
  if (!rawStatus) return '';

  if (
    rawStatus === 'completada' ||
    rawStatus === 'aprobada' ||
    rawStatus === 'approved'
  ) {
    return 'approved';
  }

  if (
    rawStatus === 'en_progreso' ||
    rawStatus === 'in_progress' ||
    rawStatus === 'inprogress'
  ) {
    return 'in_progress';
  }

  if (rawStatus === 'rechazada' || rawStatus === 'rejected') {
    return 'rejected';
  }

  if (rawStatus === 'sin_asignar' || rawStatus === 'unassigned') {
    return 'unassigned';
  }

  return rawStatus;
};

const parseNodeType = (value) => {
  if (typeof value === 'number') return value;
  if (typeof value !== 'string') return null;
  const normalized = value.trim().toLowerCase();
  if (normalized === 'task') return NODE_TYPE.TASK;
  if (normalized === 'approval') return NODE_TYPE.APPROVAL;
  const asNumber = Number(value);
  return Number.isFinite(asNumber) ? asNumber : null;
};

const parseNodeStatus = (value) => {
  if (typeof value === 'number') return value;
  const asNumber = Number(value);
  return Number.isFinite(asNumber) ? asNumber : null;
};

const parseWorkflowStatus = (value) => {
  if (typeof value === 'number') return value;
  if (typeof value !== 'string') return null;
  const normalized = value.trim().toLowerCase();
  if (normalized === 'inprogress') return WORKFLOW_STATUS.IN_PROGRESS;
  if (normalized === 'approvedworkflow' || normalized === 'approved') return WORKFLOW_STATUS.APPROVED;
  if (normalized === 'rejected') return WORKFLOW_STATUS.REJECTED;
  if (normalized === 'finalized') return WORKFLOW_STATUS.FINALIZED;
  const asNumber = Number(value);
  return Number.isFinite(asNumber) ? asNumber : null;
};

const resolveEffectiveTaskStatus = (taskItem, wfInstance) => {
  const baseStatus = resolveTaskStatus(taskItem);
  if (!wfInstance) return baseStatus;

  const wfStatus = parseWorkflowStatus(wfInstance.status);
  if (wfStatus === WORKFLOW_STATUS.APPROVED || wfStatus === WORKFLOW_STATUS.FINALIZED) {
    return 'approved';
  }
  if (wfStatus === WORKFLOW_STATUS.REJECTED) {
    return 'rejected';
  }
  if (wfStatus === WORKFLOW_STATUS.IN_PROGRESS) {
    return 'in_progress';
  }
  return baseStatus;
};

const matchesSelectedStatus = (taskItem, statusId, workflowInstance) => {
  if (statusId === null || statusId === undefined) return true;

  const effectiveStatus = resolveEffectiveTaskStatus(
    taskItem,
    workflowInstance
  );
  const statusIdNumber = Number(statusId);

  if (statusIdNumber === 1) return effectiveStatus === 'approved';
  if (statusIdNumber === 2) return effectiveStatus === 'in_progress';
  if (statusIdNumber === 4) return effectiveStatus === 'rejected';
  if (statusIdNumber === 5) return effectiveStatus === 'unassigned';

  return true;
};

const getStatusBadgeConfig = (taskItem, workflowInstance, t) => {
  const effectiveStatus = resolveEffectiveTaskStatus(
    taskItem,
    workflowInstance
  );

  if (effectiveStatus === 'approved') {
    return { text: t('status_approved'), color: 'success' };
  }
  if (effectiveStatus === 'in_progress') {
    return { text: t('status_in_progress'), color: 'warning' };
  }
  if (effectiveStatus === 'rejected') {
    return { text: t('status_rejected'), color: 'error' };
  }
  if (effectiveStatus === 'unassigned') {
    return { text: t('status_unassigned'), color: 'info' };
  }

  return {
    text: taskItem?.nombreEstado || '--',
    color: 'info',
  };
};

const getWorkflowBadgeConfig = (workflowInstance, t) => {
  const parsedStatus = parseWorkflowStatus(workflowInstance?.status);

  if (parsedStatus === WORKFLOW_STATUS.IN_PROGRESS) {
    const nodes = Array.isArray(workflowInstance?.nodes)
      ? workflowInstance.nodes
      : [];

    const hasActiveApproval = nodes.some(
      (node) =>
        parseNodeType(node?.nodeType) === NODE_TYPE.APPROVAL &&
        parseNodeStatus(node?.status) === 2
    );

    if (hasActiveApproval) {
      return { text: t('workflow_pending_approval'), color: 'warning' };
    }

    const hasActiveTask = nodes.some(
      (node) =>
        parseNodeType(node?.nodeType) === NODE_TYPE.TASK &&
        parseNodeStatus(node?.status) === 2
    );

    if (hasActiveTask) {
      return { text: t('workflow_pending_task'), color: 'warning' };
    }

    return { text: t('workflow_in_progress'), color: 'warning' };
  }

  if (parsedStatus === WORKFLOW_STATUS.APPROVED) {
    return { text: t('workflow_approved'), color: 'success' };
  }

  if (parsedStatus === WORKFLOW_STATUS.REJECTED) {
    return { text: t('workflow_rejected'), color: 'error' };
  }

  if (parsedStatus === WORKFLOW_STATUS.FINALIZED) {
    return { text: t('workflow_finalized'), color: 'info' };
  }

  return { text: t('workflow_none'), color: 'light' };
};

const formatDate = (isoDate) => {
  if (!isoDate) return '-';
  const date = new Date(isoDate);
  if (Number.isNaN(date.getTime())) return '-';

  return `${String(date.getDate()).padStart(2, '0')}/${String(
    date.getMonth() + 1
  ).padStart(2, '0')}/${date.getFullYear()}`;
};

const normalizeSortDirection = (direction) => {
  const normalized = String(direction || 'DESC')
    .trim()
    .toUpperCase();
  return normalized === 'ASC' ? 'ASC' : 'DESC';
};

const Tasks = ({ DesignSystemComponents }) => {
  const {
    Card,
    Button,
    Input,
    Dropdown,
    IconButton,
    Table,
    Badge,
    ComboBox,
    SvgIcon,
  } = DesignSystemComponents;

  const { language } = useLanguage();
  const tasksLanguage = normalizeSupportedLanguage(language);
  const { t } = useTranslation(undefined, { i18n: tasksI18n });

  useEffect(() => {
    tasksI18n.changeLanguage(tasksLanguage);
  }, [tasksLanguage]);

  const statusOptions = useMemo(() => buildStatusOptions(t), [t]);

  useEffect(() => {
    const allCourts = { label: t('filter_all_courts'), value: 0 };
    setCourts((prev) => [allCourts, ...prev.slice(1)]);
    setSelectedCourts((prev) => (prev?.value === 0 ? allCourts : prev));
    setSelectedHasDocument((prev) =>
      prev?.value === null ? { label: t('filter_all'), value: null } : prev
    );
    setSelectedPriority((prev) =>
      prev?.value === null
        ? { label: t('filter_all_priorities'), value: null }
        : prev
    );
    setSelectedHasComments((prev) =>
      prev?.value === null ? { label: t('filter_all'), value: null } : prev
    );
    setSelectedStatusOption((prev) => {
      const found = buildStatusOptions(t).find(
        (o) => o.backendId === prev?.backendId
      );
      return found || buildStatusOptions(t)[1];
    });
  }, [tasksLanguage]);
  const defaultStatusOption = statusOptions[1];

  const [userId, setUserId] = useState(null);
  const [isLoading, setIsLoading] = useState(false); // Estado de carga
  const [error, setError] = useState(null); // Estado de error
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [taskTablePage, setTaskTablePage] = useState(0);
  const [causasOptions, setCausasOptions] = useState([]);
  const [allUserOptions, setAllUserOptions] = useState([]);
  const [taskTitleOptions, setTaskTitleOptions] = useState([]);
  const [rawEstados, setRawEstados] = useState([]);
  const [rawPrioridades, setRawPrioridades] = useState([]);
  const estadoOptions = useMemo(() => {
    const translations = {
      approved: t('status_approved'),
      in_progress: t('status_in_progress'),
      rejected: t('status_rejected'),
      unassigned: t('status_unassigned'),
    };
    return rawEstados.map((s) => ({
      ...s,
      label: translations[s.label] || s.label,
    }));
  }, [rawEstados, tasksLanguage]);

  const prioridadOptions = useMemo(() => {
    const translations = {
      1: t('filter_priority_high'),
      2: t('filter_priority_medium'),
      3: t('filter_priority_low'),
    };
    return rawPrioridades.map((p) => ({
      ...p,
      label: translations[Number(p.value)] || p.label,
    }));
  }, [rawPrioridades, tasksLanguage]);
  const [isReady, setIsReady] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [businessAreaOptions, setBusinessAreaOptions] = useState([]);
  const [selectedGroupsMulti, setSelectedGroupsMulti] = useState([]);
  const [courts, setCourts] = useState(() => [
    { label: tasksI18n.t('filter_all_courts'), value: 0 },
  ]);
  const [selectedCourts, setSelectedCourts] = useState(() => ({
    label: tasksI18n.t('filter_all_courts'),
    value: 0,
  }));
  const [asignedUser, setAsignedUser] = useState();
  const [isExportingExcel, setIsExportingExcel] = useState(false);
  const [pendingTaskId, setPendingTaskId] = useState(null);
  const [isCreatingTask, setIsCreatingTask] = useState(false);
  const [selectedHasDocument, setSelectedHasDocument] = useState(() => ({
    label: tasksI18n.t('filter_all'),
    value: null,
  }));
  const [selectedPriority, setSelectedPriority] = useState(() => ({
    label: tasksI18n.t('filter_all_priorities'),
    value: null,
  }));
  const [selectedHasComments, setSelectedHasComments] = useState(() => ({
    label: tasksI18n.t('filter_all'),
    value: null,
  }));

  const { canDownloadExcel, canCreateTasks } = usePermissionsTasks();

  const location = useLocation();
  const navigate = useNavigate();

  const [searchParams] = useSearchParams();

  useEffect(() => {
    const idFromUrl = searchParams.get('id');
    const taskId = idFromUrl ? Number(idFromUrl) : null;

    if (!taskId || Number.isNaN(taskId)) return;

    setPendingTaskId(taskId);

    navigate('/tareas', { replace: true });
  }, [searchParams, navigate]);

  useEffect(() => {
    const authUser = getCurrentUser();

    if (authUser?.userId) {
      setUserId(authUser.userId);
    } else {
      console.warn('No se pudo obtener el usuario autenticado');
    }
  }, []);

  useEffect(() => {
    if (!location.state?.taskIdToOpen) return;

    setPendingTaskId(location.state.taskIdToOpen);

    navigate(location.pathname, { replace: true, state: null });
  }, [location.key]);

  useEffect(() => {
    if (!pendingTaskId || !userId) return;

    handleViewTask(pendingTaskId);
    setPendingTaskId(null);
  }, [pendingTaskId, userId]);

  useEffect(() => {
    if (!userId) return;

    const loadAll = async () => {
      try {
        const [
          states,
          priorities,
          allUsers,
          causas,
          titles,
          courtsResp,
          businessAreas,
        ] = await Promise.all([
          fetchTaskStates(),
          fetchTaskPriorities(),
          fetchAllSystemUsers(),
          getUserCases(userId),
          fetchTaskTitles(),
          getAllCourts(),
          fetchBusinessArea(),
        ]);

        setRawEstados(states);
        setRawPrioridades(priorities);
        setAllUserOptions(allUsers);
        setCausasOptions(causas);
        setTaskTitleOptions(titles);
        setCourts((prev) => [...prev, ...courtsResp]);
        setBusinessAreaOptions(businessAreas);

        setIsReady(true);
      } catch (err) {
        console.error(err);
        setError(t('msg_error_load_options'));
      }
    };

    loadAll();
  }, [userId]);

  // useEffect(() => {
  //   const loadCausas = async () => {
  //     try {
  //       const causas = await getUserCases(userId);
  //       setCausasOptions(causas);
  //     } catch (error) {
  //       console.error('Error loading causas options:', error);
  //     }
  //   };

  //   loadCausas();
  // }, []);

  const [kpiCounts, setKpiCounts] = useState({
    totalRechazadas: 0,
    totalEnProgreso: 0,
    totalCompletadas: 0,
  });

  const [showPanel, setShowPanel] = useState(false); // Panel avanzado
  const [queryParams, setQueryParams] = useState({
    filtroGlobal: '',
    statusId: statusOptions[1].backendId, // En progreso por defecto
    casoId: null,
    usuarioAsignadoId: null,
    fechaVencimiento: null,
    fechaVencimientoDesde: null,
    fechaVencimientoHasta: null,
    pageNumber: 1,
    pageSize: 10,
    sortBy: 'createdat',
    sortDirection: 'DESC',
    courts: null,
    groups: null,
    hasDocument: null,
    priorityId: null,
    hasComments: null,
  });

  const [selectedStatusOption, setSelectedStatusOption] =
    useState(defaultStatusOption);

  const [tasksData, setTasksData] = useState({
    items: [], // Los datos de TaskListItemDto irán aquí
    totalCount: 0,
    totalPages: 0,
  });

  const [workflowByTaskId, setWorkflowByTaskId] = useState({});
  const workflowLookupCacheRef = useRef(new Map());
  const workflowFeedbackTimeoutRef = useRef(null);
  const [workflowFeedback, setWorkflowFeedback] = useState(null);
  const [selectedWorkflowFlow, setSelectedWorkflowFlow] = useState(null);
  const [selectedWorkflowRequest, setSelectedWorkflowRequest] = useState(null);
  const [isWorkflowFlowLoading, setIsWorkflowFlowLoading] = useState(false);
  const [modalData, setModalData] = useState(null);
  const [isModalLoading, setIsModalLoading] = useState(false);

  const showWorkflowFeedback = useCallback((message) => {
    if (workflowFeedbackTimeoutRef.current) {
      clearTimeout(workflowFeedbackTimeoutRef.current);
      workflowFeedbackTimeoutRef.current = null;
    }

    setWorkflowFeedback(message || null);

    if (message) {
      workflowFeedbackTimeoutRef.current = setTimeout(() => {
        setWorkflowFeedback(null);
        workflowFeedbackTimeoutRef.current = null;
      }, 6000);
    }
  }, []);

  const handleViewTask = async (taskId) => {
    if (!taskId || !userId) return;

    setIsModalLoading(true);
    setModalData(null);
    setShowDetailModal(true); // Abre la modal inmediatamente para mostrar estado de carga

    try {
      const details = await fetchTaskDetailsById(taskId);
      const taskKey = String(taskId);
      let workflowInstance =
        workflowByTaskId[taskKey] ||
        (workflowLookupCacheRef.current.has(taskKey)
          ? workflowLookupCacheRef.current.get(taskKey)
          : null);

      if (!workflowInstance) {
        try {
          workflowInstance = await getWorkflowInstanceByReference(
            WORKFLOW_REFERENCE_TYPE_TASK,
            taskKey
          );
          workflowLookupCacheRef.current.set(taskKey, workflowInstance || null);
        } catch (workflowLookupError) {
          console.error(
            `Error cargando workflow para detalle de tarea ${taskId}:`,
            workflowLookupError
          );
        }
      }

      setModalData({
        ...details,
        workflowInstance: workflowInstance || null,
      });
    } catch (error) {
      console.error('Error al obtener detalles para modal:', error);
      setError(
        t('msg_error_load_details')
      );
    } finally {
      setIsModalLoading(false);
    }
  };

  const handleViewWorkflow = async (taskItem) => {
    const taskId = taskItem?.id;
    if (!taskId) return;

    const taskKey = String(taskId);
    showWorkflowFeedback(null);
    let workflowSummary = workflowByTaskId[taskKey];

    if (!workflowSummary && workflowLookupCacheRef.current.has(taskKey)) {
      workflowSummary = workflowLookupCacheRef.current.get(taskKey);
    }

    if (workflowSummary === undefined || workflowSummary === null) {
      try {
        const lookup = await getWorkflowInstanceByReference(
          WORKFLOW_REFERENCE_TYPE_TASK,
          taskKey
        );
        workflowLookupCacheRef.current.set(taskKey, lookup || null);
        workflowSummary = lookup || null;
        if (lookup) {
          setWorkflowByTaskId((prev) => ({ ...prev, [taskKey]: lookup }));
        }
      } catch (lookupError) {
        console.error(
          `Error cargando workflow para tarea ${taskKey}:`,
          lookupError
        );
        workflowLookupCacheRef.current.delete(taskKey);
        workflowSummary = undefined;
      }
    }

    if (!workflowSummary?.id) {
      setError(t('msg_error_no_workflow'));
      return;
    }

    setIsWorkflowFlowLoading(true);
    setError(null);

    try {
      const detail = await getWorkflowInstance(workflowSummary.id);
      const taskPriority =
        typeof taskItem?.priority === 'string' && taskItem.priority.trim()
          ? taskItem.priority
          : t('filter_priority_medium');

      setSelectedWorkflowFlow(detail);
      setSelectedWorkflowRequest({
        task: taskItem?.nombreTarea || t('task_fallback', { id: taskId }),
        workflow: detail?.workflowName || workflowSummary?.workflowName || '-',
        requester:
          detail?.initiatedByName ||
          (detail?.initiatedByUserId
            ? `Usuario #${detail.initiatedByUserId}`
            : '-'),
        date: formatDate(detail?.startedAtUtc),
        priority: taskPriority,
      });
    } catch (workflowError) {
      console.error('Error al cargar detalle del workflow:', workflowError);
      setError(
        t('msg_error_load_workflow')
      );
      setSelectedWorkflowFlow(null);
      setSelectedWorkflowRequest(null);
    } finally {
      setIsWorkflowFlowLoading(false);
    }
  };

  const handleGroupsMultiSelect = (selectedItems) => {
    if (!Array.isArray(selectedItems)) return;

    setSelectedGroupsMulti(selectedItems);

    const selectedValues = selectedItems.map((item) => item.value);

    setQueryParams((prev) => ({
      ...prev,
      groups: selectedValues,
    }));
  };

  useEffect(() => {
    if (!userId) return;

    invalidateWorkflowReferenceCache();
    workflowLookupCacheRef.current.clear();
    setWorkflowByTaskId({});
    showWorkflowFeedback(null);

    setQueryParams((prev) => ({
      ...prev,
      //usuarioAsignadoId: userId,
      pageNumber: 1,
    }));
  }, [userId, showWorkflowFeedback]);

  const cargarTabla = useCallback(async () => {
    if (!userId) return;

    setIsLoading(true);
    setError(null);
    showWorkflowFeedback(null);
    try {
      // Evita usar snapshots de workflow obsoletos despues de aprobar/rechazar.
      invalidateWorkflowReferenceCache();
      workflowLookupCacheRef.current.clear();
      setWorkflowByTaskId({});

      // Cargar KPIs y tabla en paralelo
      const [countsResult, result] = await Promise.all([
        fetchTaskCounts(userId).catch((countError) => {
          console.error('Error cargando KPIs:', countError);
          return null;
        }),
        fetchPagedTasks(queryParams),
      ]);

      if (countsResult) {
        setKpiCounts(countsResult);
      }

      const taskItems = result.items || [];
      setTasksData({
        items: taskItems,
        totalCount: result.totalCount || 0,
        totalPages: result.totalPages || 0,
      });
    } catch (err) {
      console.error('Error en cargarTabla:', err);
      // No mostramos mensaje de error en la UI para evitar confusión cuando no hay resultados.
      setTasksData({ items: [], totalCount: 0, totalPages: 0 });
      setWorkflowByTaskId({});
      setKpiCounts({
        totalRechazadas: 0,
        totalEnProgreso: 0,
        totalCompletadas: 0,
      });
    } finally {
      setIsLoading(false);
    }
  }, [queryParams, userId, showWorkflowFeedback]);

  useEffect(() => {
    return () => {
      if (workflowFeedbackTimeoutRef.current) {
        clearTimeout(workflowFeedbackTimeoutRef.current);
        workflowFeedbackTimeoutRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    if (!userId || !isReady) return;
    cargarTabla();
  }, [queryParams, userId, isReady, cargarTabla]);

  const handleSearchChange = (event) => {
    setQueryParams((prevParams) => ({
      ...prevParams,
      filtroGlobal: event.target.value,
      pageNumber: 1, // Reinicia página
    }));
  };

  const handleCasesChange = (e) => {
    const value = e.target.value; // ✔ correcto para Input

    setQueryParams((prevParams) => ({
      ...prevParams,
      casoId: value, // ✔ guarda texto del caso
      pageNumber: 1, // ✔ reinicia página
    }));
  };

  const handleStatusChange = (selectedOption) => {
    setSelectedStatusOption(selectedOption);
    setQueryParams((prevParams) => ({
      ...prevParams,
      statusId: selectedOption.backendId,
      pageNumber: 1, // Reinicia página
    }));
  };

  const handlePageChange = (eventData) => {
    const pageIndex =
      typeof eventData === 'object' ? eventData.page : eventData;

    const pageNumberForApi = pageIndex + 1;

    if (pageNumberForApi !== queryParams.pageNumber) {
      setTaskTablePage(pageIndex); // <<<<< NECESARIO
      setQueryParams((prev) => ({
        ...prev,
        pageNumber: pageNumberForApi,
      }));
    }
  };

  const handlePageSizeChange = (eventData) => {
    const newPageSize =
      typeof eventData === 'object' ? eventData.value : eventData;

    if (
      newPageSize !== queryParams.pageSize &&
      !isNaN(newPageSize) &&
      newPageSize > 0
    ) {
      setQueryParams((prevParams) => ({
        ...prevParams,
        pageSize: newPageSize,
        pageNumber: 1,
      }));
    } else {
      console.warn(
        '[handlePageSizeChange] El tamaño de página no cambió o el valor es inválido.'
      );
    }
  };

  const fetchBusinessArea = async () => {
    try {
      const resp = await getCreateInfo();

      return resp.businessArea
        .filter((u) => u.id !== 2 && u.id !== 11)
        .map((u) => ({
          label: u.name,
          value: u.id,
          ...u,
        }));
    } catch (error) {
      console.error(error);
      return [];
    }
  };

  const handleCreateTask = async (taskDataPayload) => {
    setIsCreatingTask(true);
    try {
      await createTask(taskDataPayload);
      await cargarTabla();
      setShowCreateModal(false);
      clearLocationState();
    } catch (error) {
      console.error('Error al crear la tarea:', error);
      setError(t('msg_error_create_task'));
    } finally {
      setIsCreatingTask(false);
    }
  };

  const handleFechaVencimientoChange = (e) => {
    const fecha = e.target.value;
    setQueryParams((prev) => ({
      ...prev,
      fechaVencimiento: fecha,
      pageNumber: 1,
    }));
  };

  const handleFechaVencimientoDesdeChange = (e) => {
    setQueryParams((prev) => ({
      ...prev,
      fechaVencimientoDesde: e.target.value,
      pageNumber: 1,
    }));
  };

  const handleFechaVencimientoHastaChange = (e) => {
    setQueryParams((prev) => ({
      ...prev,
      fechaVencimientoHasta: e.target.value,
      pageNumber: 1,
    }));
  };

  const handleApplyFilters = () => {
    // For tasks, filters are applied in real time via queryParams.
    // Here we force a refresh from page 1 to ensure the latest filters are reflected.
    setQueryParams((prev) => ({
      ...prev,
      pageNumber: 1,
    }));
  };

  const handleClearFilters = () => {
    // Reset advanced filters: caso, fecha, tribunal, usuario asignado
    setSelectedCourts({ label: t('filter_all_courts'), value: 0 });
    setSelectedUser(null);
    setSelectedHasDocument({ label: t('filter_all'), value: null });
    setSelectedPriority({ label: t('filter_all_priorities'), value: null });
    setSelectedHasComments({ label: t('filter_all'), value: null });

    setQueryParams((prev) => ({
      ...prev,
      casoId: null,
      fechaVencimiento: null,
      fechaVencimientoDesde: null,
      fechaVencimientoHasta: null,
      courtId: null,
      usuarioAsignadoId: null,
      hasDocument: null,
      priorityId: null,
      hasComments: null,
      pageNumber: 1,
    }));
  };

  const handleFilterChange = (field, value) => {
    setQueryParams((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleCourtsFilter = (selected) => {
    setSelectedCourts(selected ?? { label: t('filter_all_courts'), value: 0 });

    setQueryParams((prev) => ({
      ...prev,
      courtId: selected ? selected.value : null, // <--- un int
    }));
  };

  const buildFilterChips = () => {
    const chips = [];

    if (queryParams.filtroGlobal) {
      chips.push({
        id: 'search',
        label: t('chip_search'),
        value: queryParams.filtroGlobal,
      });
    }

    if (
      selectedStatusOption &&
      selectedStatusOption.backendId !== defaultStatusOption.backendId
    ) {
      chips.push({
        id: 'status',
        label: t('chip_status'),
        value: selectedStatusOption.label,
      });
    }

    if (queryParams.casoId) {
      chips.push({
        id: 'casoId',
        label: t('chip_case'),
        value: queryParams.casoId,
      });
    }

    if (queryParams.fechaVencimiento) {
      chips.push({
        id: 'fechaVencimiento',
        label: t('chip_due_date'),
        value: queryParams.fechaVencimiento,
      });
    }

    if (queryParams.courtId) {
      chips.push({
        id: 'courtId',
        label: t('chip_court'),
        value: selectedCourts?.label || t('chip_court_selected'),
      });
    }

    if (queryParams.usuarioAsignadoId) {
      chips.push({
        id: 'usuarioAsignadoId',
        label: t('chip_assigned'),
        value: selectedUser?.label || t('chip_user_selected'),
      });
    }

    if (queryParams.hasDocument !== null) {
      chips.push({
        id: 'hasDocument',
        label: t('chip_has_document'),
        value: queryParams.hasDocument ? t('filter_yes') : t('filter_no'),
      });
    }

    if (queryParams.priorityId) {
      chips.push({
        id: 'priorityId',
        label: t('chip_priority'),
        value: selectedPriority?.label || t('chip_priority_selected'),
      });
    }

    if (queryParams.hasComments !== null) {
      chips.push({
        id: 'hasComments',
        label: t('chip_has_comments'),
        value: queryParams.hasComments ? t('filter_yes') : t('filter_no'),
      });
    }

    return chips;
  };

  const handleRemoveFilterChip = (id) => {
    switch (id) {
      case 'search': {
        setQueryParams((prev) => ({
          ...prev,
          filtroGlobal: '',
          pageNumber: 1,
        }));
        break;
      }
      case 'status': {
        setSelectedStatusOption(defaultStatusOption);
        setQueryParams((prev) => ({
          ...prev,
          statusId: defaultStatusOption.backendId,
          pageNumber: 1,
        }));
        break;
      }
      case 'casoId': {
        setQueryParams((prev) => ({
          ...prev,
          casoId: null,
          pageNumber: 1,
        }));
        break;
      }
      case 'fechaVencimiento': {
        setQueryParams((prev) => ({
          ...prev,
          fechaVencimiento: null,
          pageNumber: 1,
        }));
        break;
      }
      case 'courtId': {
        setSelectedCourts({ label: t('filter_all_courts'), value: 0 });
        setQueryParams((prev) => ({
          ...prev,
          courtId: null,
          pageNumber: 1,
        }));
        break;
      }
      case 'usuarioAsignadoId': {
        setSelectedUser(null);
        setQueryParams((prev) => ({
          ...prev,
          usuarioAsignadoId: null,
          pageNumber: 1,
        }));
        break;
      }
      case 'hasDocument': {
        setSelectedHasDocument({ label: t('filter_all'), value: null });
        setQueryParams((prev) => ({
          ...prev,
          hasDocument: null,
          pageNumber: 1,
        }));
        break;
      }
      case 'priorityId': {
        setSelectedPriority({ label: t('filter_all_priorities'), value: null });
        setQueryParams((prev) => ({
          ...prev,
          priorityId: null,
          pageNumber: 1,
        }));
        break;
      }
      case 'hasComments': {
        setSelectedHasComments({ label: t('filter_all'), value: null });
        setQueryParams((prev) => ({
          ...prev,
          hasComments: null,
          pageNumber: 1,
        }));
        break;
      }
      default:
        break;
    }
  };

  const handleClearAllFilterChips = () => {
    setSelectedCourts({ label: t('filter_all_courts'), value: 0 });
    setSelectedUser(null);
    setSelectedStatusOption(defaultStatusOption);
    setSelectedHasDocument({ label: t('filter_all'), value: null });
    setSelectedPriority({ label: t('filter_all_priorities'), value: null });
    setSelectedHasComments({ label: t('filter_all'), value: null });

    setQueryParams((prev) => ({
      ...prev,
      filtroGlobal: '',
      statusId: defaultStatusOption.backendId,
      casoId: null,
      fechaVencimiento: null,
      courtId: null,
      usuarioAsignadoId: null,
      hasDocument: null,
      priorityId: null,
      hasComments: null,
      pageNumber: 1,
    }));
  };

  const isFromMonitoringSac =
    location.state?.fromMonitoring === true &&
    location.state?.preselectedTaskType === 'SAC';

  useEffect(() => {
    if (location.state?.openTaskModal) {
      setShowCreateModal(true);
    }
  }, [location.state]);

  const clearLocationState = useCallback(() => {
    navigate(location.pathname, { replace: true, state: null });
  }, [navigate, location.pathname]);

  const handleRemoveUser = async (item, removedUserId) => {
    try {
      await removeUserFromTask(item.id, removedUserId, userId);

      setAsignedUser('');
    } catch (error) {
      console.error('Error eliminando usuario:', error);
    }
  };

  const handleExportExcel = async () => {
    try {
      setIsExportingExcel(true);
      await exportTasksExcel({
        ...queryParams,
        sortKey: queryParams.sortKey,
        sortDirection: queryParams.sortDirection,
      });
    } catch (error) {
      console.error('Error descargando Excel:', error);
    } finally {
      setIsExportingExcel(false);
    }
  };

  return (
    <div>
      <div className='mb-4 d-flex justify-content-between'>
        <div>
          <h2 className=' fw-semibold'>{t('tasks_title')}</h2>
          <h4 className=' fw-normal'>{t('tasks_subtitle')}</h4>
        </div>
        <Button
          label={t('tasks_new_task')}
          size='sm'
          variant='primary'
          iconStart='plusLg'
          onClick={() => setShowCreateModal(true)}
          disabled={!canCreateTasks}
        />
      </div>
      {/* KPI CARDS */}
      <div className='row mb-4'>
        <div className='col-12 col-md-4'>
          <CardStats
            DesignSystemComponents={DesignSystemComponents}
            title={t('kpi_rejected_title')}
            count={kpiCounts.totalRechazadas}
            subtitle={isLoading ? t('kpi_loading') : t('kpi_rejected_subtitle')}
            icon='close'
            type='error'
          />
        </div>
        <div className='col-12 col-md-4'>
          <CardStats
            DesignSystemComponents={DesignSystemComponents}
            title={t('kpi_in_progress_title')}
            count={kpiCounts.totalEnProgreso}
            subtitle={isLoading ? t('kpi_loading') : t('kpi_in_progress_subtitle')}
            type='warning'
            icon={'clock'}
          />
        </div>
        <div className='col-12 col-md-4'>
          <CardStats
            DesignSystemComponents={DesignSystemComponents}
            title={t('kpi_completed_title')}
            count={kpiCounts.totalCompletadas}
            subtitle={isLoading ? t('kpi_loading') : t('kpi_completed_subtitle')}
            icon={'check2Circle'}
            type='success'
            fill
          />
        </div>
      </div>

      <Card specialPadding='p-4'>
        {/* HEADER */}
        <div className='d-flex justify-content-end mb-4'></div>

        {/* Muestra mensaje de error si existe */}
        {error && <div className='alert alert-danger'>{error}</div>}
        {workflowFeedback && (
          <div
            className='alert alert-danger alert-dismissible fade show'
            role='alert'
          >
            {workflowFeedback}
            <button
              type='button'
              className='btn-close'
              aria-label='Cerrar'
              onClick={() => showWorkflowFeedback(null)}
            />
          </div>
        )}

        {/* BUSCADOR Y FILTROS */}
        <div className='row'>
          <div className='col-7'>
            <Input
              icon='search'
              name='searchTask'
              onChange={handleSearchChange} // Llama al manejador
              placeholder={t('filter_search_placeholder')}
              type='text'
              value={queryParams.filtroGlobal} // Conecta al estado
            />
          </div>
          <div className='col-3'>
            <div className='d-flex flex-row'>
              <div className='flex-grow-1 me-4'>
                <Dropdown
                  placeholder={t('filter_status_placeholder')}
                  onChange={handleStatusChange}
                  options={statusOptions}
                  value={selectedStatusOption} // Conecta al estado visual
                />
              </div>
              <IconButton
                ariaLabel='filtrar'
                icon='funnel'
                onClick={() => setShowPanel((prev) => !prev)}
                className='mt-1'
                size='lg'
              />
            </div>
          </div>
          <div className='col-2'>
            <div className='d-flex justify-content-end'>
              <Button
                iconStart='download'
                label={t('filter_download_excel')}
                variant='naked'
                className='mt-2'
                size='sm'
                onClick={handleExportExcel}
                disabled={!canDownloadExcel || isExportingExcel}
              />
            </div>
          </div>
        </div>
        {!showPanel && (
          <FilterChips
            filters={buildFilterChips()}
            onRemove={handleRemoveFilterChip}
            onClearAll={handleClearAllFilterChips}
          />
        )}
        {/* PANEL DE FILTROS AVANZADOS */}
        {showPanel && (
          <ExpandablePanel
            open={showPanel}
            showActions
            onApply={handleApplyFilters}
            onClear={handleClearFilters}
            onClose={() => setShowPanel(false)}
          >
            <div className='d-flex align-items-center justify-content-between mb-3'>
              <div>
                <h4 className='fw-semibold'>{t('filter_panel_title')}</h4>
                <p className='mini font-light fw-normal'>
                  {t('filter_panel_subtitle')}
                </p>
              </div>

              <IconButton
                ariaLabel={t('filter_close_panel')}
                icon='close'
                size='md'
                onClick={() => setShowPanel(false)}
              />
            </div>

            <div className='row'>
              <div className='col-4'>
                <Input
                  label={t('filter_label_case')}
                  placeholder={t('filter_placeholder_case')}
                  type='text'
                  name='caso'
                  value={queryParams.casoId || ''}
                  onChange={handleCasesChange}
                />
              </div>

              <div className='col-4'>
                <Input
                  label={t('filter_label_due_date_from')}
                  type='date'
                  value={queryParams.fechaVencimientoDesde || ''}
                  onChange={handleFechaVencimientoDesdeChange}
                />
              </div>

              <div className='col-4'>
                <Input
                  label={t('filter_label_due_date_to')}
                  type='date'
                  value={queryParams.fechaVencimientoHasta || ''}
                  onChange={handleFechaVencimientoHastaChange}
                  min={queryParams.fechaVencimientoDesde || undefined}
                />
              </div>

              {/* <div className='col-4'>
                <Dropdown
                  label='Tipo creación'
                  placeholder='Selecciona'
                  options={[
                    { label: 'Manual', value: 'manual' },
                    { label: 'Automática', value: 'automatica' },
                    { label: 'Monitor', value: 'monitor' },
                  ]}
                />
              </div> */}
              <div className='col-4'>
                <ComboBox
                  label={t('filter_label_area')}
                  placeholder={t('filter_placeholder_area')}
                  options={businessAreaOptions}
                  value={selectedGroupsMulti}
                  onChange={handleGroupsMultiSelect}
                  multiSelect
                />
              </div>
            </div>

            <div className='row py-3'>
              <div className='col-4'>
                <div style={{ marginBottom: '20px' }}>
                  <ComboBox
                    label={t('filter_label_court')}
                    placeholder={t('filter_placeholder_court')}
                    options={courts}
                    onChange={handleCourtsFilter}
                    value={selectedCourts}
                  />
                </div>
              </div>

              <div className='col-4'>
                <div style={{ marginBottom: '20px' }}>
                  <ComboBox
                    label={t('filter_label_assigned')}
                    placeholder={t('filter_placeholder_assigned')}
                    options={allUserOptions}
                    value={selectedUser}
                    onChange={(selected) => {
                      setSelectedUser(selected);
                      const nextUserId =
                        selected?.backendId ??
                        (selected?.value ? Number(selected.value) : null) ??
                        null;
                      setQueryParams((prev) => ({
                        ...prev,
                        usuarioAsignadoId: nextUserId,
                      }));
                    }}
                  />
                </div>
              </div>
              <div className='col-4'>
                <Dropdown
                  label={t('filter_label_has_comments')}
                  placeholder={t('filter_placeholder_select')}
                  options={[
                    { label: t('filter_all'), value: null },
                    { label: t('filter_yes'), value: true },
                    { label: t('filter_no'), value: false },
                  ]}
                  value={selectedHasComments}
                  onChange={(selected) => {
                    setSelectedHasComments(selected);
                    setQueryParams((prev) => ({
                      ...prev,
                      hasComments: selected?.value ?? null,
                      pageNumber: 1,
                    }));
                  }}
                />
              </div>
            </div>

            <div className='row mb-3'>
              <div className='col-4'>
                <Dropdown
                  label={t('filter_label_has_document')}
                  placeholder={t('filter_placeholder_select')}
                  options={[
                    { label: t('filter_all'), value: null },
                    { label: t('filter_yes'), value: true },
                    { label: t('filter_no'), value: false },
                  ]}
                  value={selectedHasDocument}
                  onChange={(selected) => {
                    setSelectedHasDocument(selected);
                    setQueryParams((prev) => ({
                      ...prev,
                      hasDocument: selected?.value ?? null,
                      pageNumber: 1,
                    }));
                  }}
                />
              </div>

              <div className='col-4'>
                <Dropdown
                  label={t('filter_label_priority')}
                  placeholder={t('filter_placeholder_priority')}
                  options={[
                    { label: t('filter_all_priorities'), value: null },
                    { label: t('filter_priority_high'), value: 1 },
                    { label: t('filter_priority_medium'), value: 2 },
                    { label: t('filter_priority_low'), value: 3 },
                  ]}
                  value={selectedPriority}
                  onChange={(selected) => {
                    setSelectedPriority(selected);
                    setQueryParams((prev) => ({
                      ...prev,
                      priorityId: selected?.value ?? null,
                      pageNumber: 1,
                    }));
                  }}
                />
              </div>
            </div>
          </ExpandablePanel>
        )}

        {/* TABLA */}
        <div className='table-container table-tasks'>
          <Table
            columns={[
              { accessor: 'id', label: t('col_task_number'), sort: true },
              { accessor: 'nombreTarea', label: t('col_task_name'), sort: true },
              {
                accessor: 'casoRol',
                label: t('col_case_rol'),
                sort: true,
                render: (info) =>
                  info.businessArea === 'SAC' ||
                  info.businessArea === 'EEPP' ||
                  info.casoRol === ''
                    ? '--'
                    : info.casoRol,
              },
              // {
              //   accessor: 'tipoCreacion',
              //   label: 'Tipo creación',
              //   sort: true,
              //   render: (
              //     info // info ahora es el objeto TaskListItemDto completo
              //   ) => (
              //     <Badge
              //       text={info.tipoCreacion}
              //       color={
              //         info.tipoCreacion === 'Automática'
              //           ? 'info'
              //           : info.tipoCreacion === 'Monitor'
              //             ? 'warning'
              //             : 'light'
              //       }
              //       size='sm'
              //     />
              //   ),
              // },
              {
                accessor: 'fechaVencimiento',
                label: t('col_due_date'),
                sort: true,
                render: (info) =>
                  info.fechaVencimiento
                    ? new Date(info.fechaVencimiento).toLocaleDateString()
                    : '--', // Formatea fecha
              },
              {
                accessor: 'nombreEstado',
                label: t('col_status'),
                sort: true,
                render: (info) => {
                  const workflowInstance = workflowByTaskId[info.id];
                  const statusBadge = getStatusBadgeConfig(
                    info,
                    workflowInstance,
                    t
                  );
                  return (
                    <Badge
                      text={statusBadge.text}
                      color={statusBadge.color}
                      size='sm'
                    />
                  );
                },
              },
              {
                accessor: 'templateId',
                label: t('col_document'),
                render: (info) => (info.templateId ? t('col_document_yes') : '--'),
              },
              {
                accessor: 'businessArea',
                label: t('col_area'),
                sort: true,
              },
              {
                accessor: 'court',
                label: t('col_court'),
                sort: true,
                render: (info) => (
                  <span>
                    {info.courtName ||
                      info.tribunal ||
                      info.nombreTribunal ||
                      info.tribunalNombre ||
                      '--'}
                  </span>
                ),
              },
              {
                id: 'workflowStatus',
                label: t('col_workflow'),
                render: (rowInfo) => {
                  const workflowInstance = workflowByTaskId[rowInfo.id];
                  if (!workflowInstance) return '--';

                  const workflowBadge =
                    getWorkflowBadgeConfig(workflowInstance, t);

                  return (
                    <Badge
                      text={workflowBadge.text}
                      color={workflowBadge.color}
                      size='sm'
                    />
                  );
                },
              },
              {
                id: 'actions',
                label: t('col_actions'),
                render: (rowInfo) => (
                  <div className='d-flex align-items-center gap-2'>
                    <IconButton
                      ariaLabel={t('col_view_details')}
                      icon='eye'
                      size='md'
                      onClick={() => handleViewTask(rowInfo.id)}
                    />
                    <button
                      type='button'
                      className='task-workflow-link-btn'
                      onClick={() => handleViewWorkflow(rowInfo)}
                      disabled={isWorkflowFlowLoading}
                    >
                      <SvgIcon
                        name='eye'
                        size='14px'
                        stroke='var(--primary-500)'
                      />
                      {t('col_view_flow')}
                    </button>
                  </div>
                ),
              },
            ]}
            data={tasksData.items}
            page={(queryParams.pageNumber || 1) - 1}
            onPageChange={handlePageChange}
            pageSize={queryParams.pageSize}
            onPageSizeChange={handlePageSizeChange}
            paginationMode='server'
            totalItems={tasksData.totalCount}
            paginator
            paginatorGroup={[
              { label: '10', value: 10 },
              { label: '20', value: 20 },
              { label: '50', value: 50 },
              { label: '100', value: 100 },
            ]}
            loading={isLoading}
            serverSortKey={queryParams.sortBy}
            serverSortDirection={queryParams.sortDirection}
            onServerSortChange={({ sortKey, direction }) => {
              const nextSortKey = sortKey || 'createdat';
              const nextSortDirection = normalizeSortDirection(direction);
              const currentSortDirection = normalizeSortDirection(
                queryParams.sortDirection
              );

              if (
                queryParams.sortBy === nextSortKey &&
                currentSortDirection === nextSortDirection &&
                queryParams.pageNumber === 1
              ) {
                return;
              }

              setTaskTablePage(0);
              setQueryParams((prev) => ({
                ...prev,
                sortBy: nextSortKey,
                sortDirection: nextSortDirection,
                pageNumber: 1,
              }));
            }}
          />
          {/* Mensaje si no hay datos y no está cargando */}
          {!isLoading && !error && tasksData.items.length === 0 && (
            <p className='text-center mt-3'>
              {t('msg_no_tasks')}
            </p>
          )}
        </div>
      </Card>

      {isModalLoading && <p>{t('msg_loading_details')}</p>}
      <DetailTaskModal
        DesignSystemComponents={DesignSystemComponents}
        show={showDetailModal}
        value={modalData}
        onClose={() => {
          setShowDetailModal(false);
          setModalData(null);
          setError(null);
          cargarTabla();
        }}
        onRemoveUser={handleRemoveUser}
        asignedUser={asignedUser}
        setAsignedUser={setAsignedUser}
        currentUserId={userId}
        Card={Card}
      />

      <ApprovalFlowModal
        DesignSystemComponents={DesignSystemComponents}
        show={!!selectedWorkflowFlow}
        flow={selectedWorkflowFlow}
        request={selectedWorkflowRequest}
        onClose={() => {
          setSelectedWorkflowFlow(null);
          setSelectedWorkflowRequest(null);
        }}
      />

      <ManageTaskModal
        DesignSystemComponents={DesignSystemComponents}
        show={showCreateModal}
        onClose={() => {
          setShowCreateModal(false);
          clearLocationState();
        }}
        onSave={handleCreateTask}
        taskTitles={taskTitleOptions}
        estados={estadoOptions}
        prioridades={prioridadOptions}
        causasIniciales={causasOptions} // <<< Pasa las causas
        usuarios={allUserOptions} // <<< Pasa TODOS los usuarios
        currentUserId={userId}
        /* SOLO CUANDO VIENE DESDE "CREAR TAREA SAC" */
        defaultTaskType={isFromMonitoringSac ? 'sac' : null}
        preloadedClient={
          isFromMonitoringSac ? location.state?.preselectedClient : null
        }
        preloadedDescription={
          isFromMonitoringSac ? location.state?.preloadedDescription : null
        }
        fromMonitoring={isFromMonitoringSac}
        monitoringId={isFromMonitoringSac ? location.state?.monitoringId : null}
        disableClientSelection={isFromMonitoringSac}
        isCreating={isCreatingTask}
      />
    </div>
  );
};

export default Tasks;
